import { useEffect, useRef, useState } from 'react';

interface Options {
  /** Intersection threshold (0–1). Default 0.12. */
  threshold?: number;
  /** Disconnect observer after first reveal. Default true. */
  once?: boolean;
}

/**
 * Tracks whether a DOM element has entered the viewport.
 * Used by the Reveal component for scroll-triggered entrance animations.
 * Returns a ref to attach and a boolean `visible` flag.
 */
export function useReveal<T extends Element = HTMLDivElement>(
  options: Options = {},
) {
  const { threshold = 0.12, once = true } = options;
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, once]);

  return { ref, visible };
}
